export type BrandColor = {
  id: 'primary' | 'secondary' | 'accent' | 'coral';
  name: string;
  hex: string;
};

/**
 * @deprecated V1 consulting-era palette — superseded 2026-08-29 by the
 * canonical tokens in src/index.css (v3 daylight editorial; see
 * /priorities-ai-design-language/brand-identity, where this palette is shown
 * as archived). Kept because older decks and the archive pages still cite it.
 * Do not use for new surfaces.
 *
 * ── Importer audit, 2026-08-30 (conflict №07) ─────────────────────────────
 * All seven remaining importers are ARCHIVE surfaces that display this palette
 * *as history*, which is what they are for. Re-pointing them at current tokens
 * would destroy the delta they exist to show, so the audit closes here rather
 * than in a migration:
 *
 *   Colors.tsx                    behind a superseded banner
 *   VisualDesignGrammar.tsx       behind a superseded banner
 *   DesignSystemArchive.tsx       the archive itself
 *   PrioritiesAILegacyPage.tsx    the one Legacy page, behind the Deprecated banner
 *                                 (2026-10-01: moved out of the Colors and Brand
 *                                 identity pages, which no longer import it)
 *   SourceOfTruthChecker.tsx      compares palettes; needs both by definition
 *
 * A NEW importer that is not an archive surface is the defect this deprecation
 * exists to catch. Re-run: grep -rl brandColors src/
 */
export const brandColors: Record<BrandColor['id'], BrandColor> = {
  /** Tailwind alias `slate-blue` — green/sage hues; not chromatic blue. */
  primary: { id: 'primary', name: 'Sage Green', hex: '#5A7D5E' },
  secondary: { id: 'secondary', name: 'Alpine Green', hex: '#53675B' },
  accent: { id: 'accent', name: 'Electric Mustard', hex: '#E3B505' },
  coral: { id: 'coral', name: 'Luminous Coral', hex: '#F85E5E' },
};

/**
 * Peak atmospheric blue (“Frosted Sky” anchor in Tailwind `sky`).
 * Structural blue register for decks, gradients, diagrams — complements Sage primary CTAs.
 */
export const PEAK_FROSTED_SKY_ANCHOR = '#AFC6D9';

/** Full Frosted Sky ramp (consulting visuals / extended complements). Anchor at sky-40/300 (`#AFC6D9`). */
export const PEAK_FROSTED_SKY_RAMP_HEX = [
  '#FFFFFF',
  '#F8FBFF',
  '#E8F1F9',
  '#D6E4F0',
  '#AFC6D9',
  '#9BB3C7',
  '#87A0B5',
  '#738DA3',
  '#5F7A91',
  '#3D4B5C',
] as const;

export const getBrandColor = (id: BrandColor['id']) => brandColors[id];


